import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import useBranches from "@/hooks/useBranches";
import { fmtDate } from "@/lib/format";
import {
  Plus,
  PencilSimple,
  Key,
  Power,
  X,
  CheckCircle,
} from "@phosphor-icons/react";

function StatusPill({ active }) {
  return (
    <span
      data-testid={`user-active-${active ? "yes" : "no"}`}
      className={`inline-flex items-center px-2 py-0.5 border text-[10px] font-mono uppercase tracking-wider ${
        active
          ? "border-[#00B341] bg-[#00B341]/10 text-[#00B341]"
          : "border-destructive bg-destructive/10 text-destructive"
      }`}
    >
      {active ? "Active" : "Deactivated"}
    </span>
  );
}

function UserFormDrawer({ open, editing, roles, branches, onClose, onSaved }) {
  const blank = {
    name: "",
    email: "",
    password: "",
    role_id: "",
    branch_id: "",
    phone: "",
  };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        name: editing.name || "",
        email: editing.email || "",
        password: "",
        role_id: editing.role_id || "",
        branch_id: editing.branch_id || "",
        phone: editing.phone || "",
      });
    } else {
      setForm({ ...blank, role_id: roles.find((r) => r.name === "staff")?.id || "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  if (!open) return null;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/auth/users/${editing.id}`, {
          name: form.name,
          email: form.email,
          role_id: form.role_id || null,
          branch_id: form.branch_id || null,
          phone: form.phone,
        });
        toast.success("User updated");
      } else {
        const role = roles.find((r) => r.id === form.role_id);
        await api.post("/auth/register", {
          name: form.name,
          email: form.email,
          password: form.password || "changeme123",
          role: role?.name === "admin" ? "admin" : "staff",
          role_id: form.role_id || null,
          branch_id: form.branch_id || null,
          phone: form.phone,
        });
        toast.success("User created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/40" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        data-testid="user-form-drawer"
        className="w-[440px] bg-canvas-card border-l-2 border-ink overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-canvas-line flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
              {editing ? "Edit user" : "New user"}
            </div>
            <h2 className="font-display text-xl font-bold text-ink mt-1">
              {editing ? editing.name : "Invite team member"}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 border border-canvas-line hover:border-ink">
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Name
            </label>
            <input
              required
              value={form.name}
              onChange={(e) => set("name")(e.target.value)}
              data-testid="user-name"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Email
            </label>
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => set("email")(e.target.value)}
              data-testid="user-email"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Mobile <span className="normal-case tracking-normal">(printed on DSE quotes)</span>
            </label>
            <input
              inputMode="numeric"
              value={form.phone}
              onChange={(e) => set("phone")(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="10-digit mobile"
              data-testid="user-phone"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
            />
          </div>
          {!editing && (
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                Initial password
              </label>
              <input
                required
                value={form.password}
                onChange={(e) => set("password")(e.target.value)}
                placeholder="min. 6 chars · share verbally"
                data-testid="user-password"
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
              />
            </div>
          )}
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Role
            </label>
            <select
              value={form.role_id}
              onChange={(e) => set("role_id")(e.target.value)}
              data-testid="user-role"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            >
              <option value="">— Select role —</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} {r.is_builtin ? "(built-in)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Branch
            </label>
            <select
              value={form.branch_id}
              onChange={(e) => set("branch_id")(e.target.value)}
              data-testid="user-branch"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            >
              <option value="">— Unassigned (admin can see all) —</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div className="pt-4 border-t border-canvas-line flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 border border-canvas-line text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="user-save"
              className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Save" : "Create user"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function PasswordResetDialog({ open, user, onClose, onDone }) {
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setPwd("");
  }, [open]);

  if (!open) return null;
  const onSubmit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/auth/users/${user.id}/reset-password`, { new_password: pwd });
      toast.success("Password reset");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        data-testid="reset-password-dialog"
        className="bg-canvas-card border-2 border-ink p-6 w-full max-w-md space-y-4"
      >
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
            Reset Password
          </div>
          <h2 className="font-display text-xl font-bold text-ink mt-1">{user.name}</h2>
          <div className="font-mono text-[10px] text-ink-muted mt-1">{user.email}</div>
        </div>
        <input
          type="text"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder="New temporary password (≥ 6 chars)"
          data-testid="reset-password-input"
          className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
        />
        <p className="font-mono text-[10px] text-ink-subtle">
          Share verbally — the user will use this on their next login.
        </p>
        <div className="flex justify-end gap-2 pt-2 border-t border-canvas-line">
          <button type="button" onClick={onClose} className="px-3 py-2 border border-canvas-line text-sm">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="reset-password-save"
            className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-60"
          >
            {saving ? "Saving…" : "Reset"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const { branches } = useBranches();
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([
        api.get("/auth/users"),
        api.get("/roles"),
      ]);
      setUsers(u.data);
      setRoles(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const branchById = useMemo(
    () => Object.fromEntries((branches || []).map((b) => [b.id, b])),
    [branches],
  );
  const roleNameById = useMemo(
    () => Object.fromEntries(roles.map((r) => [r.id, r.name])),
    [roles],
  );

  const handleToggleActive = async (u) => {
    try {
      await api.post(`/auth/users/${u.id}/active`, { active: !(u.active !== false) });
      toast.success(u.active !== false ? "User deactivated" : "User activated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        eyebrow="09 / Team"
        title="Users"
        subtitle="Invite team members, assign roles, and manage access."
        actions={
          <button
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
            data-testid="add-user-button"
            className="bg-ink text-white px-4 py-2.5 text-sm font-semibold tracking-tight hover:bg-brand transition-colors duration-150 flex items-center gap-2"
          >
            <Plus size={14} weight="bold" />
            New User
          </button>
        }
      />

      <div className="px-8 py-6">
        <div className="bg-canvas-card border border-canvas-line overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="border-b border-canvas-line bg-canvas-panel">
                  {["Name", "Email", "Role", "Branch", "Status", "Last login", ""].map((h) => (
                    <th
                      key={h || "act"}
                      className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && users.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">
                      No users yet.
                    </td>
                  </tr>
                )}
                {users.map((u) => (
                  <tr
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    className="border-b border-canvas-line hover:bg-canvas-panel/40"
                  >
                    <td className="px-4 py-3 font-semibold text-ink">{u.name}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-ink-muted">{u.email}</td>
                    <td className="px-4 py-3 text-sm capitalize">
                      {roleNameById[u.role_id] || u.role}
                    </td>
                    <td className="px-4 py-3 text-sm text-ink-muted">
                      {u.branch_id ? branchById[u.branch_id]?.name || "—" : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill active={u.active !== false} />
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-ink-muted">
                      {u.last_login_at ? fmtDate(u.last_login_at) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => {
                            setEditing(u);
                            setDrawerOpen(true);
                          }}
                          data-testid={`edit-user-${u.id}`}
                          title="Edit user"
                          className="p-1.5 border border-canvas-line hover:border-ink"
                        >
                          <PencilSimple size={12} weight="bold" />
                        </button>
                        <button
                          onClick={() => setResetTarget(u)}
                          data-testid={`reset-pw-${u.id}`}
                          title="Reset password"
                          className="p-1.5 border border-canvas-line hover:border-ink"
                        >
                          <Key size={12} weight="bold" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(u)}
                          data-testid={`toggle-active-${u.id}`}
                          title={u.active !== false ? "Deactivate" : "Activate"}
                          className={`p-1.5 border ${
                            u.active !== false
                              ? "border-destructive text-destructive hover:bg-destructive hover:text-white"
                              : "border-[#00B341] text-[#00B341] hover:bg-[#00B341] hover:text-white"
                          }`}
                        >
                          {u.active !== false ? (
                            <Power size={12} weight="bold" />
                          ) : (
                            <CheckCircle size={12} weight="bold" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <UserFormDrawer
        open={drawerOpen}
        editing={editing}
        roles={roles}
        branches={branches || []}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => {
          setDrawerOpen(false);
          load();
        }}
      />

      {resetTarget && (
        <PasswordResetDialog
          open={!!resetTarget}
          user={resetTarget}
          onClose={() => setResetTarget(null)}
          onDone={() => setResetTarget(null)}
        />
      )}
    </div>
  );
}
